import { Box } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type { MonthlyEstimation } from "../models/MonthlyEstimation";

// Series colors carried over from the legacy IHBox chart for visual parity.
// Charts are exempt from the mesfColors rule — distinct, high-contrast series
// colors are semantically meaningful and replicating the legacy palette
// minimizes operator confusion during the cutover.
const COLOR_ESTIMATED = "#175adc";
const COLOR_ACTUAL = "#f89406";

const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

interface Props {
	rows: MonthlyEstimation[];
}

export const ForecastChart = ({ rows }: Props) => {
	const option = useMemo(() => {
		const byMonth = new Map<number, MonthlyEstimation>();
		for (const r of rows) byMonth.set(r.Number, r);

		const estimated: (number | null)[] = [];
		const actual: (number | null)[] = [];
		for (let m = 1; m <= 12; m++) {
			const row = byMonth.get(m);
			estimated.push(row?.ForecastedProduction ?? null);
			actual.push(row?.ActualProduction ?? null);
		}

		return {
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			animationEasingUpdate: "cubicOut",
			tooltip: {
				trigger: "axis",
				valueFormatter: (v: number | null) =>
					v == null
						? "—"
						: new Intl.NumberFormat("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(Number(v)),
			},
			legend: {
				show: true,
				top: 4,
				left: "center",
				icon: "circle",
				itemGap: 16,
				textStyle: { fontSize: 11 },
			},
			grid: { left: 60, right: 24, top: 36, bottom: 32 },
			xAxis: {
				type: "category",
				data: MONTH_LABELS,
				axisLabel: { fontSize: 11 },
			},
			yAxis: {
				type: "value",
				axisLabel: {
					formatter: (v: number) =>
						v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v),
				},
			},
			series: [
				{
					name: "Estimated",
					type: "line",
					data: estimated,
					itemStyle: { color: COLOR_ESTIMATED },
					lineStyle: {
						color: COLOR_ESTIMATED,
						width: 2,
						type: "dashed" as const,
					},
					symbolSize: 7,
					connectNulls: true,
				},
				{
					name: "Actual",
					type: "line",
					data: actual,
					itemStyle: { color: COLOR_ACTUAL },
					lineStyle: { color: COLOR_ACTUAL, width: 2 },
					symbolSize: 7,
					connectNulls: true,
				},
			],
		};
	}, [rows]);

	return (
		<Box sx={{ width: "100%", height: "100%" }}>
			<ReactECharts
				option={option}
				notMerge={false}
				lazyUpdate
				style={{ width: "100%", height: "100%" }}
			/>
		</Box>
	);
};
